import React from 'react';
import { Chip, ChipProps, Stack, Tooltip } from '@mui/material';

import { ConsentState, ConsentType, ResourceConsentsSummary } from 'types/legal';
import { formatLegalDate, formatLegalDateTime, platformLabel } from './legalUtils';

type ChipColor = ChipProps['color'];

export interface ConsentDescription {
  label: string;
  color: ChipColor;
  detail: string;
}

const versionsText = (state: ConsentState) => {
  const parts = [
    state.privacyVersion ? `Privacy Policy v${state.privacyVersion}` : '',
    state.termsVersion ? `Terms v${state.termsVersion}` : ''
  ].filter(Boolean);
  return parts.join(', ');
};

// One wording for a consent state everywhere: resource dialogs, history, report.
export const describeConsent = (state: ConsentState | null | undefined, type: ConsentType, enabled = true): ConsentDescription => {
  if (!state || state.status === 'MISSING') {
    if (!enabled) return { label: 'Not required', color: 'default', detail: 'This checkbox is switched off for the client.' };
    return type === 'SMS'
      ? { label: 'Not recorded', color: 'error', detail: 'No SMS consent from any app or admin yet.' }
      : { label: 'Not accepted', color: 'error', detail: "They'll be asked at their next login." };
  }

  const when = formatLegalDateTime(state.at);
  const byAdmin = state.source === 'ADMIN';
  const admin = state.recordedBy?.name ? `Recorded by ${state.recordedBy.name} · Admin` : 'Recorded by an admin';

  if (state.status === 'WITHDRAWN') {
    return {
      label: 'Withdrawn',
      color: 'warning',
      detail: [when, byAdmin ? admin : platformLabel(state.platform) && `via ${platformLabel(state.platform)}`].filter(Boolean).join(' · ')
    };
  }

  const detail = byAdmin
    ? [admin, when].filter(Boolean).join(' · ')
    : [when, platformLabel(state.platform) && `via ${platformLabel(state.platform)}`, type === 'PRIVACY_TERMS' ? versionsText(state) : '']
        .filter(Boolean)
        .join(' · ');
  return { label: 'Accepted', color: 'success', detail };
};

// Compact read of ResourceData.consents for list rows — no request. Nothing on record
// reads as "missing" in a neutral tone: the row can't tell whether the client has the
// checkbox switched on.
export const ConsentBadges: React.FC<{ consents?: ResourceConsentsSummary | null; hasPhone: boolean }> = ({ consents, hasPhone }) => {
  const terms = consents?.privacyTerms;
  const sms = consents?.sms;

  const badge = (key: string, label: string, color: ChipColor, title: string) => (
    <Tooltip key={key} title={title} arrow>
      <Chip
        label={label}
        size="small"
        color={color}
        variant={color === 'default' ? 'outlined' : 'filled'}
        sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500 }}
      />
    </Tooltip>
  );

  const since = (at?: string | null, platform?: any, source?: any) =>
    [formatLegalDate(at), source === 'ADMIN' ? 'recorded by an admin' : platformLabel(platform) && `via ${platformLabel(platform)}`]
      .filter(Boolean)
      .join(' · ');

  const badges = [
    !terms
      ? badge('terms', 'Terms missing', 'default', 'No Privacy Policy & Terms consent on record')
      : terms.granted
      ? badge('terms', 'Terms accepted', 'success', since(terms.at, terms.platform, terms.source) || 'Accepted')
      : badge('terms', 'Terms withdrawn', 'warning', since(terms.at, terms.platform, terms.source) || 'Withdrawn'),
    hasPhone &&
      (!sms
        ? badge('sms', 'SMS missing', 'default', 'No SMS consent on record')
        : sms.granted
        ? badge(
            'sms',
            sms.source === 'ADMIN' ? 'SMS by admin' : 'SMS accepted',
            'success',
            since(sms.at, sms.platform, sms.source) || 'Accepted'
          )
        : badge('sms', 'SMS withdrawn', 'warning', since(sms.at, sms.platform, sms.source) || 'Withdrawn'))
  ];

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap aria-label="Consent">
      {badges}
    </Stack>
  );
};
